import { computed, onMounted, ref } from 'vue';
import { useNuxtApp } from '#app';
import type { BaseService } from "@/_api/services/base";
import type { BaseModel } from "@/_api/models/base";

export const useEditModel = <T extends BaseModel>(
  {
    id,
    service,
    immediate = true,
    onBeforeLoad,
    onAfterLoad,
    onLoadError,
    onBeforeSubmit,
    onAfterSubmit,
    onSubmitError,
    successNotification = true
  }: {
    id: string;
    service: BaseService<T>;
    immediate?: boolean;
    callback?: (response: any) => void;
    successNotification?: boolean;
    onBeforeLoad?: () => void;
    onAfterLoad?: (data: T) => void;
    onLoadError?: (error: any) => void;
    onBeforeSubmit?: (data: T) => void;
    onAfterSubmit?: (response: any, data: T) => void;
    onSubmitError?: (error: any, data: T) => void;
  },
) => {
  const { $notification, $i18n } = useNuxtApp();

  const data = ref<T>();
  const original = ref<T>();
  const saving = ref(false);
  const loading = ref(false);
  const errors = ref<any>();

  const fetchData = async () => {
    try {
      loading.value = true;
      
      if (onBeforeLoad) {
        onBeforeLoad();
      }

      let response;
      if (id === 'new') {
        response = await service.defaults({} as any);
      } else {
        response = await service.get(id);
      }

      data.value = response.data;

      if (onAfterLoad && data.value) {
        onAfterLoad(data.value);
      }

      // API models are JSON data, and serialising first also unwraps any Vue
      // proxies before creating the detached change-tracking snapshot.
      const snapshot = JSON.parse(JSON.stringify(data.value.toJSON()));

      original.value = new (response.data.constructor as { new(data: any): T })(snapshot);
    } catch (error) {
      errors.value = error;
      
      if (onLoadError) {
        onLoadError(error);
      }
    } finally {
      loading.value = false;
    }
  };

  const onSubmit = async () => {
    try {
      saving.value = true;

      if (onBeforeSubmit && data.value) {
        onBeforeSubmit(data.value);
      }

      let response;

      if (data.value) {
        response = await service.save(data.value);
      }

      if (successNotification) {
        $notification.toast({
          title: $i18n.t('general.success'),
          text: $i18n.t('general.successInfo'),
          variant: 'success',
        })
      }

      if (onAfterSubmit && data.value) {
        onAfterSubmit(response, data.value);
      }

      saving.value = false;
    } catch (error: any) {
      if (onSubmitError && data.value) {
        onSubmitError(error, data.value);
      }

      if (error.data?.data?.name === "ZodError") {
        const issues = error.data.data.issues
          .map((issue: any) => {
            const path = issue.path.join(".");
            return `Invalid ${path}: ${issue.message}`;
          })
          .join("\n");
        $notification.error({
          title: "Validation Error",
          text: issues,
        });
      } else {
        $notification.error({
          title: error.data?.statusMessage || error.message || "An unknown error occurred",
        });
      }
    } finally {
      saving.value = false;
    }
  };

  const changed = computed(() => {
    return JSON.stringify(data.value) !== JSON.stringify(original.value);
  });
  
  onMounted(async () => {
    if(immediate) {
      await fetchData()
    }
  })

  return {
    data,
    saving,
    loading,
    changed,
    onSubmit,
    fetchData,
  };
};
